import { taxAcronym as getTaxAcronym } from '@/helpers/vatRegions'

import { useMemo } from 'react'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { getCutOffLocations } from '../../helpers/validation'
import { getClosestVendorData } from '../components/input-bar/getClosestVendorData'
import { InRangeLocations } from '../hooks/useAddressInRangeCheck'

interface VendorLocationState {
  inRangeVendorLocations: InRangeLocations | null

  // Actions
  setInRangeVendorLocations: (locations: InRangeLocations | null) => void
}

const initialState = {
  inRangeVendorLocations: null,
}

const useVendorStore = create<VendorLocationState>()(
  devtools(
    immer((set) => ({
      ...initialState,

      // Actions
      setInRangeVendorLocations: (locations) =>
        set((state) => {
          state.inRangeVendorLocations = locations
        }),
    })),
    {
      name: 'vendor-location-store',
    },
  ),
)

type VendorLocationOptions = {
  useDeliveryTimes?: boolean
}

const deriveVendorLocationData = (
  inRangeVendorLocations: InRangeLocations | null,
  options?: VendorLocationOptions,
) => {
  const useDeliveryTimes = options?.useDeliveryTimes ?? false
  const locations = inRangeVendorLocations ?? []
  const availableVendorLocations = getCutOffLocations(locations, useDeliveryTimes)
  const closestLocation = availableVendorLocations[0] ?? null
  const {
    deliveryFeeExTax = 0,
    minOrderValueExTax = null,
    minNoticePeriod = null,
  } = getClosestVendorData(availableVendorLocations)
  const taxAcronym = closestLocation ? getTaxAcronym(closestLocation.country ?? undefined) : null

  return {
    selectedVendorLocation: closestLocation,
    deliveryFee: deliveryFeeExTax ?? 0,
    deliveryFeeExTax,
    minOrderValueExTax,
    minNoticePeriod,
    taxAcronym,
    availableVendorLocations,
    inRangeVendorLocations,
  }
}

export const useVendorLocationData = (options?: VendorLocationOptions) => {
  const inRangeVendorLocations = useVendorStore((state) => state.inRangeVendorLocations)
  const useDeliveryTimes = options?.useDeliveryTimes ?? false
  // Cutoff rules depend on the local hour as well as the location data.
  const cutoffHour = useDeliveryTimes ? new Date().setMinutes(0, 0, 0) : null

  return useMemo(
    () => deriveVendorLocationData(inRangeVendorLocations, { useDeliveryTimes }),
    [inRangeVendorLocations, useDeliveryTimes, cutoffHour],
  )
}

export const useVendorLocationActions = () => {
  const setInRangeVendorLocations = useVendorStore((state) => state.setInRangeVendorLocations)

  return {
    setInRangeVendorLocations,
  }
}
